import { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import apiClient from "./apiClient";

const ORDERS_BASE = () => process.env.REACT_APP_URL_ORDERS_SERVICE;

export type MarkRegistrySearchRequest = {
  markCode?: string;
  status?: string[];
  kmTypes?: string[];
  productTypeCodes?: string[];
  productName?: string;
  ntin?: string;
  generationDateStart?: string;
  generationDateEnd?: string;
  orgIinBin?: string;
  orgName?: string;
};

export type MarkRegistryItem = {
  id: number;
  markCode: string;
  productTypeCode?: string;
  productTypeName?: string;
  ntin?: string;
  /** List API: gtinName; keep ntinName as alias if backend renames */
  gtinName?: string;
  ntinName?: string;
  generatedAt?: string;
  status?: string;
  kmType?: string;
  childMarksCount?: number | null;
  statusAssignedAt?: string;
  withdrawalReason?: string | null;
  ownerIinBin?: string;
  issuerIinBin?: string;
};

export type MarkMovementItem = {
  eventAt?: string;
  eventName?: string;
  documentId?: number | null;
  documentName?: string | null;
  documentNumber?: string | null;
  orderNumber?: string | null;
  orderId?: number | null;
  sender?: string | null;
  senderName?: string | null;
  receiver?: string | null;
  receiverName?: string | null;
  checkId?: number | null;
  fiscalDocumentNumber?: number | string | null;
  checkType?: string | null;
};

export type MarkOrganizationInfo = {
  iinBin?: string;
  nameRu?: string;
  nameKk?: string;
};

export type MarkCardResponse = {
  id: number;
  markCode: string;
  productTypeCode?: string;
  productTypeName?: string;
  ntin?: string;
  orderNumber?: string | null;
  status?: string;
  kmType?: string;
  childMarksCount?: number | null;
  generatedAt?: string;
  statusAssignedAt?: string;
  withdrawalReason?: string | null;
  weight?: number | null;
  size?: number | null;
  owner?: MarkOrganizationInfo | null;
  issuer?: MarkOrganizationInfo | null;
  nktCard?: any;
  statusHistory?: {
    status?: string;
    assignedAt?: string;
    documentId?: number | null;
  }[];
  gasInfo?: any;
  woodInfo?: any;
  derivativeInfo?: any;
};

function activeProductGroupId(): number {
  const raw = Number(Cookies.get("activeGroup") ?? "");
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
}

/** POST /marks/registry/search/{productGroupId} */
export const searchMarksRegistry = async (
  body: MarkRegistrySearchRequest = {},
  page: number = 1,
  size: number = 30,
  productGroupId?: number,
): Promise<any> => {
  const groupId = productGroupId ?? activeProductGroupId();
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const response: AxiosResponse<any> = await apiClient.post(
    `${ORDERS_BASE()}/marks/registry/search/${groupId}?${params.toString()}`,
    body,
    { withCredentials: true },
  );
  return response.data;
};

/** GET /marks/registry/{id} */
export const getMarkCard = async (id: string | number): Promise<MarkCardResponse> => {
  const response: AxiosResponse<MarkCardResponse> = await apiClient.get(
    `${ORDERS_BASE()}/marks/registry/${id}`,
    { withCredentials: true },
  );
  return response.data;
};

/** GET /marks/registry/{id}/movement */
export const getMarkMovement = async (
  id: string | number,
): Promise<MarkMovementItem[]> => {
  const response: AxiosResponse<MarkMovementItem[] | MarkMovementItem> =
    await apiClient.get(`${ORDERS_BASE()}/marks/registry/${id}/movement`, {
      withCredentials: true,
    });
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data) return [data];
  return [];
};

/** GET /marks/registry/{id}/datamatrix — image blob */
export const getMarkDatamatrix = async (id: string | number): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await apiClient.get(
    `${ORDERS_BASE()}/marks/registry/${id}/datamatrix`,
    { withCredentials: true, responseType: "blob" },
  );
  return response.data;
};

/** GET /marks/registry/{id}/children */
export const getMarkChildren = async (id: string | number): Promise<any> => {
  const response: AxiosResponse<any> = await apiClient.get(
    `${ORDERS_BASE()}/marks/registry/${id}/children`,
    { withCredentials: true },
  );
  return response.data;
};

const MarksRegistryService = {
  searchMarksRegistry,
  getMarkCard,
  getMarkMovement,
  getMarkDatamatrix,
  getMarkChildren,
};

export default MarksRegistryService;
